import { apiClient } from 'src/utils/apiClient';
// import { dateToISONoTimezone } from 'src/utils/dates';

export interface ClubCompetition {
  id: number;
  linkedId: number | null;
  name: string;
  arrivalMinutes: number | null;
  ageGroup: string;
  category: string;
  oldestDob: string | null;
  youngestDob: string | null;
  minPlayersPerTeam: number;
  maxPlayersPerTeam: number;
  event: {
    id: number | null;
    name: string | null;
    start: string | null;
    end: string | null;
    description: string | null;
    eventEntryDeadlineDate: string | null;
    recurring: boolean;
    recurringInfo: string | null;
    recurringUntilDate: string | null;
    parentId: number | null;
  };
  sport: {
    id: number;
    name: string;
  };
  product: {
    id: number | null;
    name: string | null;
    cost: number | null;
    link: string | null;
    type: string | null;
    frequency: string | null;
    firstPaymentDate: string | null;
  };
  venue: {
    id: string | null;
    name: string | null;
    address: string | null;
    city: string | null;
    postCode: string | null;
    lat: number | null;
    lng: number | null;
  };
  isPublished: number;
  termAndConditionUrl: string | null;
  secondCategoryAgeGroup: string | null;
  entryAllowed: string | null;
  paidStatus: string | null;
  competitionAllowInvoice: string | null;
  teamEntryForm: EntryFormField[];
  singleEntryForm: EntryFormField[];
  depositPaymentProduct: number | null;
  competitionDetailsAttachment: string | null;
  depositProduct: {
    id: number | null;
    name: string | null;
    cost: number | null;
    link: string | null;
    type: string | null;
    frequency: string | null;
    firstPaymentDate: string | null;
  } | null;
  teamProduct: {
    id: number | null;
    name: string | null;
    cost: number | null;
    link: string | null;
    type: string | null;
    frequency: string | null;
    firstPaymentDate: string | null;
  } | null;
  communitySessionProduct: {
    id: number | null;
    name: string | null;
    cost: number | null;
    link: string | null;
    type: string | null;
    frequency: string | null;
    firstPaymentDate: string | null;
  } | null;
  fullPrice: boolean;
  entriesCount: number;
  entriesLimit: number | null;
  eventEntryLimit: number | null;
  sessions: { start: string; end: string }[];
  sessionStart: string | null;
  sessionEnd: string | null;
}

export type ClubCompetitions = ClubCompetition[];

interface ClubCompetitionsResponse {
  result: {
    competition_id: number;
    sport_id: number;
    sport_name: string;
    venue_id: string;
    venue_name: string;
    competition_category_age_group: string;
    competition_category_type: string;
    competition_name: string;
    competition_arrive_time: number | null;
    has_competition_started: string;
    competition_min_dob_limit: DateTimeString | null;
    competition_max_dob_limit: DateTimeString | null;
    competition_min_no_player_per_team: number;
    competition_max_no_player_per_team: number;
    venue?: {
      venue_lat: string | null;
      venue_lng: string | null;
      venue_address1: string | null;
      venue_city: string | null;
      venue_postcode: string | null;
    };
    event: {
      event_description: string | null;
      event_id: number;
      event_name: string;
      event_start: DateTimeString;
      event_end: DateTimeString;
      event_entry_deadline_date: DateTimeString | null;
      attachment_1: string | null;
      event_recurring: 0 | 1;
      event_recurring_info: string | null;
      event_recurring_until_date: string | null;
      parent_id: number | null;
      competition_entry_limit: number | null;
    };
    product: {
      stripe_product_id: number;
      stripe_product_name: string;
      stripe_product_cost: number | null;
      stripe_product_link: string | null;
      stripe_product_type: string | null;
      stripe_product_frequency: string | null;
      stripe_product_first_payment_date: string | null;
    } | null;
    competition_linked_id: number | null;
    competition_is_published: 1 | 0;
    competition_term_and_condition_url: string | null;
    competition_second_category_age_group: string | null;
    competition_entry_allowed: string | null;
    competition_paid_status: string | null;
    competition_allow_invoice: string | null;
    competition_team_entry_form: string | null;
    competition_single_entry_form: string | null;
    deposit_payment_product: number | null;
    deposit_product: {
      stripe_product_id: number;
      stripe_product_name: string;
      stripe_product_cost: number | null;
      stripe_product_link: string | null;
      stripe_product_type: string | null;
      stripe_product_frequency: string | null;
      stripe_product_first_payment_date: string | null;
    } | null;
    team_product: {
      stripe_product_id: number;
      stripe_product_name: string;
      stripe_product_cost: number | null;
      stripe_product_link: string | null;
      stripe_product_type: string | null;
      stripe_product_frequency: string | null;
      stripe_product_first_payment_date: string | null;
    } | null;
    community_session_product: {
      stripe_product_id: number;
      stripe_product_name: string;
      stripe_product_cost: number | null;
      stripe_product_link: string | null;
      stripe_product_type: string | null;
      stripe_product_frequency: string | null;
      stripe_product_first_payment_date: string | null;
    } | null;
    full_price: 1 | 0;
    entries_count: number;
    competition_entries_limit: number | null;
    dates: { competition_start_date: string; competition_end_date: string }[];
  }[];
}

export interface EntryFormField {
  id: number | string;
  name: string;
  type: string;
  use: boolean;
  required: boolean;
  isDefault?: boolean;
  options?: string[];
  choiceDisplay?: string;
}

interface GetClubCompetitionsOptions {
  fromDate?: string;
  toDate?: string;
}

export async function getClubCompetitions(
  siteCode: string,
  options: GetClubCompetitionsOptions = {},
): Promise<ClubCompetitions> {
  const fromDate = options.fromDate ?? new Date().toISOString();
  const params = new URLSearchParams({
    from_date: fromDate,
  });

  if (options.toDate) {
    params.set('to_date', options.toDate);
  }

  const { result } = await apiClient<ClubCompetitionsResponse>(
    `/v1/website/competitions?${params.toString()}`,
    { siteCode },
  );

  return result.map(competition => ({
    ...(function pickSessions() {
      const sessions = [...(competition.dates || [])]
        .filter(item => Boolean(item?.competition_start_date && item?.competition_end_date))
        .sort(
          (left, right) =>
            new Date(left.competition_start_date).getTime() - new Date(right.competition_start_date).getTime(),
        )
        .map(item => ({
          start: item.competition_start_date,
          end: item.competition_end_date,
        }));
      const firstSession = sessions[0] || null;
      return {
        sessions,
        sessionStart: firstSession?.start ?? null,
        sessionEnd: firstSession?.end ?? null,
      };
    })(),
    id: competition.competition_id,
    linkedId: competition.competition_linked_id ?? null,
    name: competition.competition_name,
    arrivalMinutes: competition.competition_arrive_time ?? null,
    ageGroup: competition.competition_category_age_group,
    category: competition.competition_category_type,
    oldestDob: competition.competition_min_dob_limit,
    youngestDob: competition.competition_max_dob_limit,
    minPlayersPerTeam: competition.competition_min_no_player_per_team,
    maxPlayersPerTeam: competition.competition_max_no_player_per_team,
    event: {
      id: competition.event?.event_id || null,
      name: competition.event?.event_name || null,
      start: competition.event?.event_start || null,
      end: competition.event?.event_end || null,
      description: competition.event?.event_description || null,
      eventEntryDeadlineDate: competition.event?.event_entry_deadline_date || null,
      recurring: competition.event?.event_recurring === 1,
      recurringInfo: competition.event?.event_recurring_info || null,
      recurringUntilDate: competition.event?.event_recurring_until_date || null,
      parentId: competition.event?.parent_id || null,
    },
    sport: {
      id: competition.sport_id,
      name: competition.sport_name,
    },
    product: {
      id: competition.product?.stripe_product_id || null,
      name: competition.product?.stripe_product_name || null,
      cost: competition.product?.stripe_product_cost || null,
      link: competition.product?.stripe_product_link || null,
      type: competition.product?.stripe_product_type || null,
      frequency: competition.product?.stripe_product_frequency || null,
      firstPaymentDate: competition.product?.stripe_product_first_payment_date || null,
    },
    venue: {
      id: competition.venue_id,
      name: competition.venue_name,
      address: competition.venue?.venue_address1 || null,
      city: competition.venue?.venue_city || null,
      postCode: competition.venue?.venue_postcode || null,
      lat: competition.venue ? Number(competition.venue.venue_lat) : null,
      lng: competition.venue ? Number(competition.venue.venue_lng) : null,
    },
    isPublished: competition.competition_is_published,
    termAndConditionUrl: competition.competition_term_and_condition_url,
    secondCategoryAgeGroup: competition.competition_second_category_age_group,
    entryAllowed: competition.competition_entry_allowed,
    paidStatus: competition.competition_paid_status,
    competitionAllowInvoice: competition.competition_allow_invoice,
    teamEntryForm: competition.competition_team_entry_form
      ? (JSON.parse(competition.competition_team_entry_form) as EntryFormField[])
      : [],
    singleEntryForm: competition.competition_single_entry_form
      ? (JSON.parse(competition.competition_single_entry_form) as EntryFormField[])
      : [],
    depositPaymentProduct: competition.deposit_payment_product,
    competitionDetailsAttachment: competition.event.attachment_1,
    depositProduct: {
      id: competition.deposit_product?.stripe_product_id || null,
      name: competition.deposit_product?.stripe_product_name || null,
      cost: competition.deposit_product?.stripe_product_cost || null,
      link: competition.deposit_product?.stripe_product_link || null,
      type: competition.deposit_product?.stripe_product_type || null,
      frequency: competition.deposit_product?.stripe_product_frequency || null,
      firstPaymentDate: competition.deposit_product?.stripe_product_first_payment_date || null,
    },
    teamProduct: {
      id: competition.team_product?.stripe_product_id || null,
      name: competition.team_product?.stripe_product_name || null,
      cost: competition.team_product?.stripe_product_cost || null,
      link: competition.team_product?.stripe_product_link || null,
      type: competition.team_product?.stripe_product_type || null,
      frequency: competition.team_product?.stripe_product_frequency || null,
      firstPaymentDate: competition.team_product?.stripe_product_first_payment_date || null,
    },
    communitySessionProduct: {
      id: competition.community_session_product?.stripe_product_id || null,
      name: competition.community_session_product?.stripe_product_name || null,
      cost: competition.community_session_product?.stripe_product_cost || null,
      link: competition.community_session_product?.stripe_product_link || null,
      type: competition.community_session_product?.stripe_product_type || null,
      frequency: competition.community_session_product?.stripe_product_frequency || null,
      firstPaymentDate: competition.community_session_product?.stripe_product_first_payment_date || null,
    },
    fullPrice: competition.full_price === 1,
    entriesCount: competition.entries_count,
    entriesLimit: competition.competition_entries_limit ?? null,
    eventEntryLimit: competition.event.competition_entry_limit ?? null,
  }));
}
